// InfoIcon — a small ⓘ that opens a styled bubble on click (not a native
// title tooltip). The bubble is position:fixed under the icon, flips
// above it near the bottom of the viewport, and closes on a click
// anywhere outside it, on Escape, or when the page scrolls — there is NO
// full-screen backdrop, so the page stays scrollable while it is open.
// `text` is the explanation (blank lines = paragraphs); `title` an
// optional bold first line; `link` = { href, label } an optional
// reference opened in a new tab.

import { useEffect, useRef, useState } from 'react';
import { FONT } from './util';

export default function InfoIcon({ text, title, ui, size = 14, link = null }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const iconRef = useRef(null);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => { if (boxRef.current?.contains(e.target) || iconRef.current?.contains(e.target)) return; setOpen(false); };
    const onScroll = () => setOpen(false);
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('scroll', onScroll, true);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown, true); document.removeEventListener('scroll', onScroll, true); };
  }, [open]);
  const toggle = (e) => {
    e.stopPropagation(); e.preventDefault();
    if (open) { setOpen(false); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const vw = window.innerWidth; const vh = window.innerHeight;
    const width = Math.min(380, vw - 24);
    let left = r.left + r.width / 2 - width / 2;
    left = Math.max(12, Math.min(left, vw - 12 - width));
    const below = r.bottom + 260 < vh;
    setPos({ left, width, top: below ? r.top + r.height + 8 : undefined, bottom: below ? undefined : vh - r.top + 8, arrowX: r.left + r.width / 2 - left, below });
    setOpen(true);
  };
  const paragraphs = String(text || '').split(/\n\s*\n/).filter(Boolean);
  return (
    <>
      <span ref={iconRef} role="button" tabIndex={0} onClick={toggle} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(e); }}
        title="How is this calculated?"
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: size, border: `1.5px solid ${open ? ui.blue : ui.muted}`, background: open ? `${ui.blue}1a` : 'transparent', color: open ? ui.blue : ui.muted, fontSize: size * 0.68, fontWeight: 800, fontStyle: 'normal', textTransform: 'none', letterSpacing: 0, cursor: 'pointer', lineHeight: 1, verticalAlign: -2, fontFamily: FONT, userSelect: 'none' }}>i</span>
      {open && pos && (
        <div ref={boxRef} onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width, maxHeight: '70vh', overflowY: 'auto', zIndex: 5001, background: ui.card, color: ui.fg, border: `1px solid ${ui.border}`, borderRadius: 12, boxShadow: ui.shadowMd, padding: '12px 14px', fontFamily: FONT, fontSize: 13, lineHeight: 1.55, textTransform: 'none', letterSpacing: 0, fontWeight: 400, textAlign: 'left', whiteSpace: 'normal' }}>
          <span style={{ position: 'absolute', left: pos.arrowX - 7, [pos.below ? 'top' : 'bottom']: -7, width: 12, height: 12, background: ui.card, borderLeft: `1px solid ${ui.border}`, borderTop: `1px solid ${ui.border}`, transform: pos.below ? 'rotate(45deg)' : 'rotate(225deg)' }} />
          {title && <div style={{ fontWeight: 800, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 18, background: `${ui.blue}1a`, color: ui.blue, fontSize: 11, fontWeight: 800 }}>i</span>{title}</div>}
          {paragraphs.map((p, i) => <p key={i} style={{ margin: i ? '8px 0 0' : 0, color: ui.fg }}>{p}</p>)}
          {link && <a href={link.href} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 10, color: ui.blue, fontWeight: 700, textDecoration: 'none' }}>{link.label} ↗</a>}
        </div>
      )}
    </>
  );
}
